import * as React from 'react';
import { useActions } from '../workspaceSettingActions';
import { useAlert } from '../../Alert/alert';
import { Toggler } from '../../Toggler/toggler';
import { WYSIWYGEditor } from '../../WYSIWYGEditor/WYSIWYGEditor';
import { Save, Cancel } from 'rcd-cpp-ui/ui/button';
import { useConfirmModal } from '../../ConfirmModal/confirmModal';
import { useSelector } from '../../../utils/useSelector';
import { NodeType } from '../../../enums/NodeType';
import { WorkspaceSettingProps } from '../../../types';

export const OtherSettings = () => {
  const isSystemWorkspace = useSelector(
    s =>
      !!s.planning?.selectedWorkspace?.labels?.includes(NodeType.AdminWorkspace)
  );
  const impliedAsPrimary = useSelector(
    s =>
      s.planning?.selectedWorkspace?.setting?.props?.impliedAsPrimary !== false
  );
  const workspaceInformation = useSelector(
    s => s.planning?.selectedWorkspace?.setting?.props?.workspaceInformation
  );
  const demandSensingResources = useSelector(
    s => s.planning?.selectedWorkspace?.setting?.props?.demandSensingResources
  );
  const { save, Alert } = useAlert(useActions());
  const currWorkspaceInfo = workspaceInformation;
  const currDemandSensingResources = demandSensingResources;
  let [remount, setRemount] = React.useState('');
  let [remountDSR, setRemountDSR] = React.useState('');
  const { confirm, ConfirmModal: DiscardModal } = useConfirmModal({
    label: 'Discard Changes'
  });
  let [workspaceInfoBuffer, setWorkspaceInfoBuffer] =
    React.useState(workspaceInformation);
  let [demandSensingResourcesBuffer, setDemandSensingResourcesBuffer] =
    React.useState(demandSensingResources);
  const handleWorkspaceInfoChange = (val: string) => {
    if (val != currWorkspaceInfo) {
      setWorkspaceInfoBuffer(val);
    }
  };
  const handleDemandSensingResourcesChange = (val: string) => {
    if (val != currWorkspaceInfo) {
      setDemandSensingResourcesBuffer(val);
    }
  };
  let randomStr = String(Math.floor(100000 + Math.random() * 900000));
  return (
    <div className="ws-setting-content default-data scrollable">
      <Alert />
      <h6 className="ws-setting-desc">Other workspace settings</h6>
      <div className="ws-setting-togglers">
        <Toggler
          label="Show single implied data as primary"
          checked={impliedAsPrimary}
          onChange={val => save({ impliedAsPrimary: val })}
        />
        {isSystemWorkspace && (
          <>
            <LockedNodeToggler nt={NodeType.RolePriority} save={save} />
            <LockedNodeToggler nt={NodeType.PriorityJourneyStep} save={save} />
            <LockedNodeToggler nt={NodeType.Position} save={save} />
          </>
        )}
      </div>
      <h6 className="ws-setting-desc">Workspace Information</h6>
      <div className="editor-group">
        <WYSIWYGEditor
          key={remount}
          content={workspaceInfoBuffer}
          disabled={false}
          onChange={val => handleWorkspaceInfoChange(val)}
        ></WYSIWYGEditor>
        <DiscardModal></DiscardModal>
        <div className="buttons">
          <Cancel
            disabled={currWorkspaceInfo == workspaceInfoBuffer}
            onClick={e => {
              confirm(
                <>
                  <b>This will discard all of your changes.</b>
                  &nbsp;Are you sure you want to discard your changes?
                </>,
                {
                  header: 'Discard Changes',
                  onDelete: () => {
                    setWorkspaceInfoBuffer(currWorkspaceInfo);
                    setRemount(randomStr);
                  }
                }
              );
            }}
            label={'Discard'}
          ></Cancel>
          <Save
            disabled={currWorkspaceInfo == workspaceInfoBuffer}
            onClick={e => {
              save({ workspaceInformation: workspaceInfoBuffer });
            }}
          ></Save>
        </div>
      </div>
      <h6 className="ws-setting-desc">Demand Sensing Resources</h6>
      <div className="editor-group">
        <WYSIWYGEditor
          key={remountDSR}
          content={demandSensingResourcesBuffer}
          disabled={false}
          onChange={val => handleDemandSensingResourcesChange(val)}
        ></WYSIWYGEditor>
        <DiscardModal></DiscardModal>
        <div className="buttons">
          <Cancel
            disabled={
              currDemandSensingResources == demandSensingResourcesBuffer
            }
            onClick={e => {
              confirm(
                <>
                  <b>This will discard all of your changes.</b>
                  &nbsp;Are you sure you want to discard your changes?
                </>,
                {
                  header: 'Discard Changes',
                  onDelete: () => {
                    setDemandSensingResourcesBuffer(currDemandSensingResources);
                    setRemountDSR(randomStr);
                  }
                }
              );
            }}
            label={'Discard'}
          ></Cancel>
          <Save
            disabled={
              currDemandSensingResources == demandSensingResourcesBuffer
            }
            onClick={e => {
              save({ demandSensingResources: demandSensingResourcesBuffer });
            }}
          ></Save>
        </div>
      </div>
    </div>
  );
};

type LNTProps = {
  nt: NodeType;
  save: (val: Partial<WorkspaceSettingProps>) => any;
};
const LockedNodeToggler = ({ nt: nodeType, save }: LNTProps) => {
  const name = useSelector(
    s =>
      s.planning?.selectedWorkspace?.schemas.find(s => s.props.type == nodeType)
        ?.props?.name || nodeType
  );
  const key = `lockedNode${nodeType}`;
  const isLocked = useSelector(
    s => s.planning?.selectedWorkspace?.setting?.props[key]
  );

  return (
    <Toggler
      label={`Lock ${name}`}
      checked={isLocked}
      onChange={val => save({ [key]: val })}
    />
  );
};
